import api from './api';
import { LoginCredentials, AdminUser, ApiResponse } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Types for Admin Registration
export interface AdminRegisterRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  code_register: string;
}

export interface AdminRegisterResponse {
  success: boolean;
  message: string;
  admin?: {
    email: string;
    first_name: string;
    last_name: string;
    is_admin: boolean;
    is_superuser: boolean;
  };
}

// Admin API services (authentication required)
export const adminApiService = {
  // Registration (no authentication required)
  register: async (data: AdminRegisterRequest): Promise<AdminRegisterResponse> => {
    const response = await fetch(`${API_BASE_URL}/api/admin/register/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || 'Erreur lors de l\'enregistrement');
    }

    return result;
  },

  // Authentication
  login: async (credentials: LoginCredentials): Promise<{ token: string; refresh_token: string; user: AdminUser }> => {
    const response = await api.post('/api/auth/login/', credentials);
    return response.data;
  },

  // Dashboard Stats
  getDashboardStats: async (): Promise<any> => {
    const response = await api.get('/api/admin/stats/');
    return response.data;
  },

  // Job Offers Management
  getJobOffers: async (): Promise<any[]> => {
    const response = await api.get('/api/admin/jobs/');
    return response.data;
  },

  createJobOffer: async (data: any): Promise<ApiResponse<any>> => {
    const response = await api.post('/api/admin/jobs/', data);
    return response.data;
  },

  updateJobOffer: async (id: number, data: any): Promise<ApiResponse<any>> => {
    const response = await api.put(`/api/admin/jobs/${id}/`, data);
    return response.data;
  },

  deleteJobOffer: async (id: number): Promise<void> => {
    await api.delete(`/api/admin/jobs/${id}/`);
  },

  // Applications Management
  getJobApplications: async (): Promise<any[]> => {
    const response = await api.get('/api/admin/applications/job/');
    return response.data;
  },

  getSpontaneousApplications: async (): Promise<any[]> => {
    const response = await api.get('/api/admin/applications/spontaneous/');
    return response.data;
  },

  // Contact Messages
  getContactMessages: async (): Promise<any[]> => {
    const response = await api.get('/api/admin/contacts/');
    return response.data;
  },

  // Appointments
  getAppointments: async (): Promise<any[]> => {
    const response = await api.get('/api/admin/appointments/');
    return response.data;
  },

  // Newsletter Subscribers
  getNewsletterSubscribers: async (): Promise<any[]> => {
    const response = await api.get('/api/admin/newsletter/');
    return response.data;
  },

  // Services Management
  getServices: async (): Promise<any[]> => {
    const response = await api.get('/api/admin/services/');
    return response.data;
  },

  createService: async (data: any): Promise<ApiResponse<any>> => {
    const response = await api.post('/api/admin/services/', data);
    return response.data;
  },

  updateService: async (id: number, data: any): Promise<ApiResponse<any>> => {
    const response = await api.put(`/api/admin/services/${id}/`, data);
    return response.data;
  },

  deleteService: async (id: number): Promise<void> => {
    await api.delete(`/api/admin/services/${id}/`);
  },

  // Projects Management
  getProjects: async (): Promise<any[]> => {
    const response = await api.get('/api/admin/projects/');
    return response.data;
  },

  createProject: async (data: FormData): Promise<ApiResponse<any>> => {
    const response = await api.post('/api/admin/projects/', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  updateProject: async (id: number, data: FormData): Promise<ApiResponse<any>> => {
    const response = await api.put(`/api/admin/projects/${id}/`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteProject: async (id: number): Promise<void> => {
    await api.delete(`/api/admin/projects/${id}/`);
  },

  // Testimonials Management
  getTestimonials: async (): Promise<any[]> => {
    const response = await api.get('/api/admin/testimonials/');
    return response.data;
  },

  createTestimonial: async (data: FormData): Promise<ApiResponse<any>> => {
    const response = await api.post('/api/admin/testimonials/', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  updateTestimonial: async (id: number, data: FormData): Promise<ApiResponse<any>> => {
    const response = await api.put(`/api/admin/testimonials/${id}/`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteTestimonial: async (id: number): Promise<void> => {
    await api.delete(`/api/admin/testimonials/${id}/`);
  },
};
